import type { CartUI, ItemUI, OrderUI, OrderLine } from '@libs/common-node';
import { Currency, DeliveryType, Locale, getCurrency, getLocale, logger } from '@libs/core';
import type { MappedProductVariant, ProductCategory, ProductHit } from '@libs/algolia';
import { PharmacyType, getDefaultCategories } from '@libs/algolia';

import type { ItemsListItem } from '@libs/common-next';
import { logSentryError, NavigationListTypes } from '@libs/common-next';
import type { StockStatusEnum } from '@petsmart-ui/sparky';
import {
    getAvailabilityConstant,
    getDimensionCategories,
    getNavigationId,
    getNavigationName,
    getProductObject,
    getPromoDesc,
    getPromoOrigin,
    getRoughTotal,
    getStockLevelConstant,
    getSubmitSearchType,
    getViewSearchResultsType,
    mapCartItem,
    mapImpressions,
    mapItem,
    mapProductClick,
    roundTotal,
    toDollarsAmount,
    processFindingMethod,
} from './helpers';
import { getSkuFindingMethod } from '../helpers/skuFindingMethodStorage';
import type { Category, EventItem, EventTracking, Product } from '../types';
import type {
    CardClick,
    ContentfulClickProps,
    EEImpressionParams,
    PageViewParams,
    ProductTileClickParams,
    SearchFlyoutClickParams,
    Tile6UpClickProps,
    TilesClickParams,
    TrackProductListFilterProps,
    UserAuthenticationParams,
    VisualNavigationClicksParams,
} from './types';
import { Tile6UpVariant } from './types';
import { TrackingEvents } from '../enums';
import { TrackingParams } from '../constants/trackingParams';
import { dataLayerPush } from './dataLayerPush';

const currencyCode = getCurrency();

type AddedProduct = {
    algoliaHit: ProductHit;
    parameters: {
        quantity: number;
        fulfillmentMethod: DeliveryType;
        type: string;
        autoshipFrequency?: number;
        storeID?: string;
    };
};

type DataTrackingObj = {
    [key: string]: unknown;
};

type Nullable<T> = T | null | undefined;

export const trackEEAddToCart = (products: AddedProduct[], captureType: string) => {
    const result = products.map(product => {
        const { algoliaHit } = product;
        const { fulfillmentMethod, storeID, type, quantity, autoshipFrequency } = product.parameters;

        // Most reliable way found to get a subcategory path
        const defaultCategories = getDefaultCategories(product.algoliaHit);

        const deliveryTypes: Record<DeliveryType, string> = {
            [DeliveryType.BOPIS]: 'bopis',
            [DeliveryType.SDD]: 'sdd',
            [DeliveryType.STM]: 'stm',
            [DeliveryType.AUTOSHIP]: 'autoship',
            [DeliveryType.STH]: 'sth',
        };

        const productObject = getProductObject(algoliaHit);

        const dimension21 =
            algoliaHit['pharmacy-type'] === PharmacyType.MED && algoliaHit.customCaseQuantity
                ? algoliaHit.customCaseQuantity
                : null;

        return {
            ...productObject,
            price: algoliaHit.price?.number,
            quantity,
            dimension9: defaultCategories[0]?.name.default,
            dimension10: defaultCategories[1]?.name.default,
            dimension11: defaultCategories[2]?.name.default,
            dimension21,
            dimension12: deliveryTypes[fulfillmentMethod],
            dimension13: autoshipFrequency,
            dimension14: storeID,
            dimension133: captureType,
            dimension6: type, // Standard PDP
            // dimension39 is not here because it's a parameter for a SFCC thing.
        };
    });

    dataLayerPush({
        event: 'EEaddToCart',
        ecommerce: {
            currencyCode,
            add: {
                actionField: {
                    list: captureType,
                },
                products: result,
            },
        },
    });
};

export const trackProductListFilter = ({
    position,
    linkUrl,
    linkText,
    promoTitle,
    promoVariant,
    filterSelection,
    filterType,
    results,
}: TrackProductListFilterProps) => {
    dataLayerPush({
        event: 'product_list_filter',
        filter_selection: filterSelection,
        filter_type: filterType,
        position,
        link_url: linkUrl,
        link_text: linkText,
        promo_title: promoTitle,
        promo_variant: promoVariant,
        results,
    });
};

export const trackContentfulClick = ({
    position,
    linkText,
    linkUrl,
    promoTitle,
    promoVariant,
    promoOrigin,
    promoDesc,
    promoType,
}: ContentfulClickProps) => {
    const dataLayerObject: EventTracking = {
        event: 'contentful_click',
        link_text: linkText,
        link_url: linkUrl,
        promo_title: promoTitle,
        promo_variant: promoVariant,
        promo_origin: getPromoOrigin(promoOrigin),
        promo_desc: getPromoDesc(promoDesc),
        promo_type: promoType,
    };
    if (position) {
        Object.assign(dataLayerObject, {
            position,
        });
    }

    dataLayerPush({ ecommerce: null });
    dataLayerPush(dataLayerObject);
};

export const trackCardUpClick = ({
    variant,
    title,
    cardName,
    cardPosition,
    link,
    promoType,
    pathname = '',
    linkText = '',
}: CardClick) => {
    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'contentful_click',
        position: cardPosition,
        link_text: linkText ?? cardName,
        link_url: link,
        promo_title: title,
        promo_variant: variant,
        promo_origin: getPromoOrigin(pathname),
        promo_desc: getPromoDesc(cardName),
        promo_type: promoType,
    });
};

export const trackProductTileClick = ({ product, position, variant = null, list = null }: ProductTileClickParams) => {
    const actionField: { list: string | null; action?: string } = {
        list,
    };

    if (getLocale() === Locale['en-CA']) {
        actionField.action = 'click';
    }

    dataLayerPush({
        event: 'EEproductClick',
        ecommerce: {
            currencyCode,
            click: {
                actionField,
                products: [
                    {
                        ...mapProductClick(product),
                        position,
                    },
                ],
            },
            variant,
        },
    });
};

export const trackAddToCartMethod = (deliveryType: DeliveryType) => {
    const table: Record<string, string> = {
        [DeliveryType.BOPIS]: 'bopis',
        [DeliveryType.STM]: 'sth',
    };

    const value = table[deliveryType];

    if (value) {
        dataLayerPush({
            event: `add-to-cart ${value}`,
        });
    }
};

export const trackTabClick = (selection?: string) => {
    dataLayerPush({
        event: TrackingEvents.TAB_CLICK,
        selection,
    });
};

export const velocityTrackAddToCart = (
    selectedProduct: MappedProductVariant,
    currency: Currency,
    pdpType: string,
    productRecommentation: string,
    items: EventItem[]
) => {
    const filteredCategories: ProductCategory[] = getDefaultCategories(selectedProduct) ?? [];

    const categories: Category = {
        category: filteredCategories[0]?.name?.default || '',
        sub_category1: filteredCategories[1]?.name?.default || '',
        sub_category2: filteredCategories[2]?.name?.default || '',
    };

    const roundedTotal = roundTotal(getRoughTotal(items));

    dataLayerPush({
        event: 'add_to_cart',
        ecommerce: {
            ...categories,
            currency,
            value: roundedTotal,
            pdp_type: pdpType,
            product_recommendation: productRecommentation,
            items,
        },
    });
};

export const trackEEImpression = ({ products, list }: EEImpressionParams) => {
    const impressions = mapImpressions(products, list);

    dataLayerPush({
        event: 'EEimpression',
        ecommerce: {
            currencyCode,
            impressions,
        },
    });
};

export const trackEEAddToWishlist = (product: MappedProductVariant, masterProduct: ProductHit) => {
    const masterProductData = getProductObject(masterProduct);
    dataLayerPush({
        event: 'EEaddToWishlist',
        ecommerce: {
            currencyCode,
            products: [
                {
                    ...masterProductData,
                    id: product.objectID,
                    name: product.name,
                    brand: product.brand,
                    dimension18: product.color,
                    dimension19: product.size,
                    dimension20: product.flavor,
                },
            ],
        },
    });
};

export const trackClickContinueShopping = () => {
    dataLayerPush({
        event: 'click_continue_shopping',
    });
};

export const trackEEProductDetailView = (
    product: ProductHit,
    availability: Record<string, { ats: number; status: StockStatusEnum }>
) => {
    const dimension6 = TrackingParams.STANDARD_PDP;
    const stm = availability[DeliveryType.STM];
    const autoship = availability[DeliveryType.AUTOSHIP];

    const productObj = {
        ...getProductObject(product),
        ...getDimensionCategories(product),
        price: product.price?.number,
        dimension6,
        dimension23: getAvailabilityConstant(stm.ats),
        dimension25: getAvailabilityConstant(autoship.ats),
        dimension37: getStockLevelConstant(stm.status),
        dimension39: product.id === product.masterProductID ? TrackingParams.YES : TrackingParams.NO,
        dimension47: getStockLevelConstant(autoship.status),
    };

    dataLayerPush({
        event: 'EEproductDetailView',
        ecommerce: {
            currencyCode,
            detail: {
                actionField: {
                    list: dimension6,
                },
                products: [productObj],
            },
        },
    });
};

export const trackGlobalPromoBanner = (text: string) => {
    dataLayerPush({
        event: 'GlobalPromotionBannerClicks',
        global_promotion_banner_name: text,
    });
};

export const trackFeatureBannerClick = (name: string, bannerTitle: string) => {
    dataLayerPush({
        event: 'FeatureBannerClick',
        bannerclick_name: name,
        bannerTitle,
    });
};

export const trackPLPSortingOptions = (optionName: string) => {
    dataLayerPush({
        event: 'PLPSortOptions',
        sortBy: optionName,
    });
};

export const trackTile6UpClick = ({ variant, name, position, navigationClick }: Tile6UpClickProps) => {
    const event = {
        [Tile6UpVariant.SHOPPABLE]: 'ShoppableBannerClick',
        [Tile6UpVariant.PROMO]: 'SixPromoTileClick',
    }[variant];

    if (variant === Tile6UpVariant.SHOPPABLE) {
        dataLayerPush({
            event,
            shoppable_banner_name: name,
            shoppable_banner_position: position,
            navigation_click: navigationClick,
        });
    }

    if (variant === Tile6UpVariant.PROMO) {
        dataLayerPush({
            event,
            promo_banner_name: name,
            promo_banner_position: position,
            navigation_click: navigationClick,
        });
    }
};

export const trackUserAuthentication = ({
    totalTreatsPoints,
    loyaltyId,
    storeId,
    loginState,
}: UserAuthenticationParams) => {
    dataLayerPush({
        event: 'UserAuthentication',
        totalTreatsPoints,
        loyaltyId,
        storeId,
        loginState,
    });
};

export const trackPromoBannerCTAClick = (bannerTitle: string, variant: string, clickText: string) => {
    dataLayerPush({
        event: 'PromoBannerClick',
        bannerTitle,
        variant,
        clickText,
    });
};

export const trackMainNavLinkClick = (linkText: string) => {
    dataLayerPush({
        event: 'MainNavClicks',
        clickTerm: linkText,
    });
};

export const trackLegacySearchEvent = (term: string, searchAssistName?: string) => {
    dataLayerPush({
        event: TrackingEvents.LEGACY_SEARCH,
        searchType: getSubmitSearchType(searchAssistName),
        searchAssist: searchAssistName,
        searchTerm: term,
    });
};

export const trackSubmitSearchEvent = (searchTerm: string, searchAssistName?: string, searchAssistTerm?: string) => {
    dataLayerPush({
        event: TrackingEvents.SUBMIT_SEARCH,
        search_type: getSubmitSearchType(searchAssistName),
        search_assist: searchAssistName?.toString().toLowerCase(),
        search_assist_term: searchAssistTerm,
        search_term: searchTerm,
    });
};

export const trackViewSearchResultsEvent = (
    term: string,
    numberOfResults: number,
    searchType?: string,
    searchAssistName?: string,
    searchAssistTerm?: string
) => {
    dataLayerPush({
        event: TrackingEvents.VIEW_SEARCH_RESULTS,
        search_type: getViewSearchResultsType(numberOfResults, searchType),
        search_term: term,
        results: numberOfResults,
        search_assist: searchAssistName?.toLowerCase(),
        search_assist_term: searchAssistTerm,
    });
};

export const trackViewSearchResultsPdp = (term: string, results: number, searchType?: string) => {
    dataLayerPush({
        event: TrackingEvents.VIEW_SEARCH_RESULTS,
        search_type: getViewSearchResultsType(results, searchType),
        search_term: term,
        results,
    });
};

export const trackTilesClicks = ({ variant, header, title, position, row }: TilesClickParams) => {
    dataLayerPush({
        event: 'Tile Clicks',
        variant,
        title: header,
        tile_description: title,
        tile_position: position, // -1 if action link is clicked
        tile_row: row, // -1 if action link is clicked
    });
};

export const trackProductView = (productName: string) => {
    dataLayerPush({
        event: 'product view',
        pageName: productName,
    });
};

export const trackPLPFilters = (filterCategory: string, filter: string) => {
    dataLayerPush({
        event: 'PLPFilters',
        filterCategory,
        filter,
    });
};

export const trackAddToCart = () => {
    dataLayerPush({
        event: 'addToCart',
    });
};

export const trackError = (pageType: string, errorMessage: string, detail?: string) => {
    dataLayerPush({
        event: TrackingEvents.ERROR,
        site_section: pageType,
        error_message: errorMessage,
        error_detail: detail ?? '',
    });

    const errorMessagesSplit = errorMessage.split('|');

    if (errorMessagesSplit.length) {
        for (const message of errorMessagesSplit) {
            dataLayerPush({
                event: 'treatErrorMessageSplit',
                error_message: message.trim(),
                error_detail: detail ?? '',
            });
        }
    }
};

export const trackSearchFlyoutClick = ({ term, header, url }: SearchFlyoutClickParams) => {
    dataLayerPush({
        event: 'search-flyout-click',
        'search-flyout-destinationURL': url,
        'search-flyout-searchTerm': term,
        'search-flyout-sectionHeader': header,
    });
};

export const trackPDPFindingMethod = (findingMethod: string) => {
    dataLayerPush({
        event: 'pdpFindMethod',
        findingMethod,
    });
};

export const trackVisualNavigationClicks = ({ categoryId, navPos, text }: VisualNavigationClicksParams) => {
    dataLayerPush({
        event: 'VisualNavigationClicks',
        categoryId,
        navigation_position: navPos,
        itemText: text,
    });
};

export type TrackProductTileClickItemProps = {
    [key: string]: unknown;
};

export type TrackProductTileClickProps = {
    product: Product;
    ecommerce: {
        [key: string]: unknown;
        items: TrackProductTileClickItemProps[];
    };
};

export const trackProductTileItemClick = ({ product, ecommerce }: TrackProductTileClickProps) => {
    const dataObj: DataTrackingObj = {
        quantity: 1,
        size: product.itemSize ?? '',
        flavor: product.flavor?.value ?? '',
        item_variant: product.objectID,
        color: product.color?.value ?? '',
    };

    Object.assign(ecommerce.items[0], dataObj);
    delete ecommerce.items[0].index;

    const dataLayerObject: DataTrackingObj = {
        event: 'select_item',
        ecommerce: {
            currency: ecommerce.currency,
            pdp_type: NavigationListTypes.STANDARD_PDP,
            category: ecommerce.category,
            sub_category1: ecommerce.sub_category1,
            sub_category2: ecommerce.sub_category2,
            items: [
                {
                    item_name: ecommerce.items[0].item_name,
                    item_id: ecommerce.items[0].item_id,
                    quantity: ecommerce.items[0].quantity,
                    price: ecommerce.items[0].price,
                    item_category: ecommerce.items[0].item_category,
                    item_category2: ecommerce.items[0].item_category2,
                    item_category3: ecommerce.items[0].item_category3,
                    item_category4: ecommerce.items[0].item_category4,
                    item_category5: ecommerce.items[0].item_category5,
                    item_brand: ecommerce.items[0].item_brand,
                    item_variant: ecommerce.items[0].item_variant,
                    item_list_name: ecommerce.items[0].item_list_name,
                    item_list_id: ecommerce.items[0].item_list_id,
                    color: ecommerce.items[0].color,
                    size: ecommerce.items[0].size,
                    flavor: ecommerce.items[0].flavor,
                    item_search_term: ecommerce.items[0].item_search_term,
                },
            ],
        },
    };

    dataLayerPush({ ecommerce: null });
    dataLayerPush(dataLayerObject);
};

export const trackApplyFacetedFilters = (filterType: string, filterSelection: string, pageId: string) => {
    dataLayerPush({
        action: 'applyFilter_SearchResults',
        event: 'applyFacetedFilter',
        label: `${filterType}_${filterSelection}`,
        linkPageName: `ps:blip:${pageId}`,
    });
};

export const trackSignUpSubmit = () => {
    dataLayerPush({
        event: 'user-signup-submit',
    });
};

export const trackSignUpSuccess = () => {
    dataLayerPush({
        event: 'user-signup-success',
    });
};

export const trackTreatsSignUpSuccess = () => {
    dataLayerPush({
        event: 'TreatsSignupSuccess',
    });
};

export const trackPersonalizationSignUpSuccess = (personalization: string[]) => {
    dataLayerPush({
        event: 'personalization',
        personalization: personalization.join(','),
    });
};

export const trackSignUpFailure = (accountCreationFailureReason: string) => {
    dataLayerPush({
        event: 'user-signup-failed',
        accountCreationFailureReason,
    });
};

export const trackTreatsSignUpFailure = (errorMessages: string[]) => {
    const errorCount = errorMessages.length;
    dataLayerPush({
        event: 'TreatsSignupFail',
        errorCount,
        errorMessage: errorMessages[0],
    });

    trackError('create account', errorMessages.join('|'));
};

export const trackChangePassword = (section: string) => {
    dataLayerPush({
        event: 'change_password',
        site_section: section,
    });
};

export const trackResetPassword = () => {
    dataLayerPush({
        event: 'reset_password',
    });
};

export const trackOrderPageView = (params: PageViewParams) => {
    dataLayerPush({
        event: 'page_view',
        ...params,
    });
};

export const trackAccountAddToCart = (orderCurrency: string, orderLines: OrderLine[], list: string) => {
    const products = orderLines.map(line => ({
        name: line.itemDescription?.replace(/['"`]/g, '\\$&'), // Product name with escaped quotes
        id: line.itemId, // // Unique product identifier
        price: line.unitPriceAmount, // Price of the product
        brand: line.product.brand, // Merchandise brand of the product.
        quantity: line.orderedQuantity, // Quantity of the item added to the cart
        dimension12: line.fulfillmentMethod?.toLowerCase() ?? null, // Fulfillment method, options include STH, BOPIS, Autoship, SDD
        dimension6: list, // Type of PDP (Product Detail Page) add-to-cart
        dimension39: 'Yes', // Placeholder for additional attribute; set to "Yes"
        dimension19: line.itemSize ?? null, // Size of the item; null if not applicable
        dimension21: null, // Placeholder for an additional attribute, null by default
        dimension13: null, // Autoship frequency; null if not applicable
        dimension9: line.product.category, // Category of the item, e.g., "dog", "cat", "reptile", "fish"
        dimension10: line.product.subCategory1, // Subcategory of the item (e.g., Treats, Food)
        dimension14: line.shippingInfo?.shipToFacility ?? null, // Store ID for BOPIS (buy online, pick up in store), null if not applicable
        dimension11: line.product.subCategory2, // More specific subcategory
        dimension5: line.product.pharmacyType ?? 'Others', // Product type, options include Others, Vet Diet, Pharmacy
        dimension20: line.product.flavor ?? null, // Flavor of the item; null if not applicable
        dimension18: line.product.color ?? null, // Color of the item; null if not applicable
        dimension41: line.product.vicePresidentName ?? null, // Value Proposition (VP) category
        dimension42: line.product.hierarchyDivision ?? null, // Division category
        dimension43: line.product.hierarchyDepartment ?? null, // Department category
        dimension44: line.product.hierarchyClass ?? null, // Class within the department
        dimension45: line.product.hierarchyCategory ?? null, // Specific category within the class
    }));

    // Clear previous ecommerce data in dataLayer
    dataLayerPush({
        ecommerce: null,
    });

    // Push new ecommerce data for an "add to cart" event
    dataLayerPush({
        event: 'EEaddToCart',
        ecommerce: {
            currencyCode: orderCurrency,
            add: {
                actionField: {
                    list,
                    action: 'add',
                },
                products,
            },
        },
    });
};

export const trackViewEmptyCart = () => {
    dataLayerPush({
        event: 'view_empty_cart',
    });
};

export const trackAddCartDonation = (currency: Currency, value: number, itemsCount = 0) => {
    const order = itemsCount + 1;

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'add_to_cart',
        ecommerce: {
            currency,
            value: (value / 100).toFixed(2),
            pdp_type: 'cart',
            category: 'donation',
            add_to_cart_type: 'add',
            items: [
                {
                    item_name: 'donation',
                    item_id: '1234',
                    quantity: 1,
                    price: (value / 100).toFixed(2),
                    item_category: 'donation',
                    item_cart_order: order,
                    add_to_cart_type: 'add',
                },
            ],
        },
    });
};

export const trackViewCart = (items: ItemsListItem[], currencyCode = Currency.USD) => {
    const products = items.map(mapCartItem);

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'EEviewCart',
        ecommerce: {
            currencyCode,
            checkout: {
                products,
            },
        },
    });
};

export const velocityTrackViewCart = (items: TrackCartItem[], currency = Currency.USD) => {
    const roundedTotal = roundTotal(getRoughTotal(items));
    const itemsWithDollarsAmount = items.map(item => ({
        ...item,
        price: toDollarsAmount(item.price),
    }));

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'view_cart',
        ecommerce: {
            currency,
            value: toDollarsAmount(roundedTotal),
            items: itemsWithDollarsAmount,
        },
    });
};

export const trackCartUpdateQuantity = (item: ItemUI, currencyCode = Currency.USD) => {
    const products = [mapItem(item)];

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'EEaddToCart',
        ecommerce: {
            currencyCode,
            add: {
                actionField: {
                    list: 'Cart',
                    action: 'add',
                },
                products,
            },
        },
    });
};

export type TrackCartItem = {
    item_name: string;
    quantity: number;
    price: number;
    item_category: string;
    item_category2: string;
    item_category3: string;
    item_category4: string;
    item_category5: string;
    item_brand: string;
    item_variant: string;
    item_list_name: string;
    item_list_id: string;
    fulfillment_method: string;
    autoship_frequency: string;
    store_id: string | null;
    color: string;
    size: string;
    flavor: string;
    item_search_term: string;
    add_to_cart_type?: string;
    autoship_availability: string;
    autoship_stock_availability?: string;
    sth_availability?: string;
    bopis_availability?: string;
    sdd_availability?: string;
    sth_stock_availability?: string;
    bopis_stock_availability?: string;
    sdd_stock_availability?: string;
};

export const velocityTrackUpdateQuantity = (currency: Currency | undefined, items: TrackCartItem[]) => {
    const roundedTotal = roundTotal(getRoughTotal(items));
    const itemsWithDollarsAmount = items.map(item => ({
        ...item,
        price: toDollarsAmount(item.price),
    }));

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'add_to_cart',
        ecommerce: {
            currency,
            value: toDollarsAmount(roundedTotal),
            pdp_type: 'cart',
            category: '', // Skipped at this momment, in the future it would be integrated
            sub_category1: '', // Skipped at this momment, in the future it would be integrated
            sub_category2: '', // Skipped at this momment, in the future it would be integrated
            add_to_cart_type: 'increase quantity',
            items: itemsWithDollarsAmount,
        },
    });
};

export const trackCartFulfillmentMethodChange = (payload: {
    cart: Nullable<CartUI>;
    item: ItemUI;
    previousFulfillmentMethod: DeliveryType | string;
    selectedFulfillmentMethod: DeliveryType | string;
}) => {
    const { cart, item, previousFulfillmentMethod, selectedFulfillmentMethod } = payload;
    dataLayerPush({
        event: 'change_fulfillment_method',
        value: item.salePrice,
        currency: cart?.currency,
        old_selection: previousFulfillmentMethod?.toLowerCase(),
        new_selection: selectedFulfillmentMethod?.toLowerCase(),
        product_id: item.sku,
        product_price: (item.salePrice / item.quantity).toFixed(2),
        product_quantity: item.quantity,
    });
};

export const trackAutoshipSetup = (
    payload: { cart: Nullable<CartUI>; item: ItemUI },
    options?: { omitSubscriptionId?: boolean }
) => {
    const { cart, item } = payload;
    const { omitSubscriptionId } = options || {};

    const eventPayload: Record<string, unknown> = {
        event: 'click_autoship_setup',
        value: item.salePrice,
        currency: cart?.currency,
        product_id: item.sku,
        product_price: (item.salePrice / item.quantity).toFixed(2),
        product_quantity: item.quantity,
        ...(!omitSubscriptionId && { subscription_id: item?.orderGrooveSubscriptionId ?? 'new' }),
    };

    dataLayerPush(eventPayload);
};

export const trackViewAutoshipInfoModal = (payload: { site_section: string }) => {
    dataLayerPush({
        event: 'view_autoship_info_modal',
        site_section: payload.site_section,
    });
};

export const trackRemoveAutoship = (payload: { cart: Nullable<CartUI>; item: ItemUI; linkText?: string }) => {
    const { cart, item, linkText } = payload;
    dataLayerPush({
        event: 'remove_autoship_modal',
        link_text: linkText,
        value: item.salePrice,
        currency: cart?.currency,
        subscription_id: item?.orderGrooveSubscriptionId ?? 'new',
        product_id: item.sku,
        product_price: (item.salePrice / item.quantity).toFixed(2),
        product_quantity: item.quantity,
    });
};

export const trackChangeSubscriptionFrequency = (payload: {
    cart: Nullable<CartUI>;
    item: ItemUI;
    previousFrequency: string;
    selectedFrequency: string;
}) => {
    const { cart, item, previousFrequency, selectedFrequency } = payload;
    dataLayerPush({
        event: 'change_subscription_frequency',
        value: item.salePrice,
        currency: cart?.currency,
        subscription_id: item?.orderGrooveSubscriptionId ?? 'new',
        old_selection: previousFrequency,
        new_selection: selectedFrequency,
        product_id: item.sku,
        product_price: (item.salePrice / item.quantity).toFixed(2),
        product_quantity: item.quantity,
    });
};

export const trackAddToSubscription = (payload: {
    cart: Nullable<CartUI>;
    item: ItemUI;
    frequency: string;
    autoshipStockAvailability: string;
}) => {
    const { cart, item, frequency, autoshipStockAvailability } = payload;
    dataLayerPush({
        event: 'add_to_subscription',
        value: item.salePrice,
        currency: cart?.currency,
        subscription_id: item?.orderGrooveSubscriptionId ?? 'new',
        subscription_type: 'add_to_existing',
        subscription_frequency: frequency,
        product_id: item.sku,
        product_price: (item.salePrice / item.quantity).toFixed(2),
        product_quantity: item.quantity,
        autoship_stock_availability: autoshipStockAvailability,
        autoship_autoselected: '',
        product_brand: item.merchandiseBrand,
        product_category: item.mhVicePresidentName,
        product_category2: item.mhDivision,
        product_category3: item.mhDepartment,
        product_category4: item.mhClass,
        product_category5: item.mhCategory,
    });
};

export const trackSaveAutoshipSetup = (payload: { cart: Nullable<CartUI>; item: ItemUI; frequency: string }) => {
    const { cart, item, frequency } = payload;
    dataLayerPush({
        event: 'save_autoship_setup',
        value: item.salePrice,
        currency: cart?.currency,
        subscription_id: item?.orderGrooveSubscriptionId ?? 'new',
        subscription_frequency: frequency,
        product_id: item.sku,
        product_price: (item.salePrice / item.quantity).toFixed(2),
        product_quantity: item.quantity,
    });
};

export const trackRemoveCart = (item: ItemUI) => {
    const products = [mapItem(item)];

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'EEremoveFromCart',
        ecommerce: {
            remove: {
                products,
            },
        },
    });
};

export const velocityTrackRemoveFromCart = (currency: Currency | undefined, items: TrackCartItem[]) => {
    const roundedTotal = roundTotal(getRoughTotal(items));

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'remove_from_cart',
        ecommerce: {
            currency,
            value: roundedTotal,
            pdp_type: 'cart',
            category: '', // Skipped at this momment, in the future it would be integrated
            sub_category1: '', // Skipped at this momment, in the future it would be integrated
            sub_category2: '', // Skipped at this momment, in the future it would be integrated
            add_to_cart_type: 'decrease quantity',
            items,
        },
    });
};

export const trackClearCart = () => {
    dataLayerPush({ ecommerce: null });

    dataLayerPush({
        event: 'clearCart',
    });
};

export const trackBeginCheckout = (cart: CartUI) => {
    dataLayerPush({ ecommerce: null });

    const items = cart.fulfillmentGroups.flatMap(group =>
        group.items.map(item => {
            // Look up the finding method for this specific SKU
            const skuFindingMethod = getSkuFindingMethod(item.sku);
            const findingMethod = processFindingMethod(skuFindingMethod);

            return {
                item_name: item.name,
                item_id: item.masterProductKey ?? null,
                quantity: item.quantity,
                price: item.totalPrice,
                item_brand: item.merchandiseBrand,
                fulfillment_method: group.fulfillmentMethod?.toLowerCase(),
                autoship_frequency: item.autoshipFrequency,
                store_id: group.storeId,
                size: item.variant?.size,
                flavor: item.variant?.flavor,
                color: item.variant?.color,
                item_cart_order: item.positionWithinCart,
                item_category: item.mhVicePresidentName,
                item_category2: item.mhDivision,
                item_category3: item.mhDepartment,
                item_category4: item.mhClass,
                item_category5: item.mhCategory,
                item_variant: item.sku,
                default_version: item.masterProductKey,

                item_list_name: getNavigationName(findingMethod),
                item_list_id: getNavigationId(findingMethod),
                item_search_term: '',
            };
        })
    );

    dataLayerPush({
        event: 'begin_checkout',
        ecommerce: {
            currency: cart.currency,
            value: cart.summary?.totalAmount,
            coupon: cart.discountCodes?.[0]?.code,
            items,
        },
    });
};

export const trackCheckout = (label: string, actionField: Record<string, unknown>) => {
    const labels = {
        complete: 'Review Order',
    };
    dataLayerPush({ ecommerce: null });

    dataLayerPush({
        event: 'EEcheckout',
        eventParameters: {
            category: 'Ecommerce',
            action: 'Checkout',
            label: labels[label as keyof typeof labels] ?? label,
        },
        ecommerce: {
            checkout: {
                actionField,
            },
        },
    });
};

export const trackCheckoutOption = (actionField: Record<string, unknown>) => {
    dataLayerPush({ ecommerce: null });

    dataLayerPush({
        event: 'EEcheckoutOption',
        ecommerce: {
            checkout: {
                actionField,
            },
        },
    });
};

export const trackPurchase = (
    purchaseData: Nullable<CartUI> | OrderUI,
    actionField: Record<string, unknown>,
    isOrderConfirmation?: boolean
) => {
    dataLayerPush({ ecommerce: null });

    const products = purchaseData?.fulfillmentGroups.flatMap(group =>
        group.items.map(item => {
            // Look up the finding method for this specific SKU
            const skuFindingMethod = getSkuFindingMethod(item.sku);
            const findingMethod = processFindingMethod(skuFindingMethod);

            return {
                name: item.name,
                id: item.sku,
                price: item.basePriceWithDiscounts! / 100 / item.quantity,
                brand: item.merchandiseBrand,
                dimension18: item.variant?.color ?? null,
                dimension19: item.variant?.size ?? null,
                dimension20: item.variant?.flavor ?? null,
                quantity: item.quantity,
                dimension5: item?.pharmacyType ?? 'Others',
                dimension21: item.customCaseQuantity,
                dimension13: item.autoshipFrequency,
                dimension14: group.storeId,
                dimension12: group.fulfillmentMethod?.toLowerCase(),
                dimension39: 'Yes',
                dimension41: item?.mhVicePresidentName ?? null,
                dimension42: item?.mhDivision ?? null,
                dimension43: item?.mhDepartment ?? null,
                dimension44: item?.mhClass ?? null,
                dimension45: item?.mhCategory ?? null,
                metric4: isOrderConfirmation ? item.basePrice / 100 : item.basePrice,
                metric5: isOrderConfirmation ? item.listPrice / 100 : item.listPrice,
                add_to_cart_type: item.pliAddedInStore ? 'in store' : 'add',
                dimension133: isOrderConfirmation ? null : 'PFM - Standard Add to Cart',
                item_list_name: getNavigationName(findingMethod),
                item_list_id: getNavigationId(findingMethod),
            };
        })
    );

    const eventTrackingProps = {
        event: 'EEpurchase',
        ecommerce: {
            currencyCode: purchaseData?.currency,
            purchase: {
                products,
                actionField,
            },
        },
    };

    logger.info('EEPurchase dataLayerPush argument: %o', eventTrackingProps);

    const gtmLoaded = window.dataLayer?.some(({ event }) => event == 'loaded-gtm');

    if (!gtmLoaded) logSentryError('GTM was not loaded');

    dataLayerPush(eventTrackingProps);
};

export const trackCheckoutSignUpImpression = () => {
    dataLayerPush({
        event: 'checkout-signup-impression',
    });
};

/** Data layer events for Appointments page */

type AppointmentEventProps = {
    service: string;
    breed?: string;
    selection?: string;
};

const trackAppointmentEvent = (event: string, { service, breed = '', selection }: AppointmentEventProps) => {
    if (event === 'click_upcoming_appt') {
        dataLayerPush({ event, selection, service });
    } else {
        dataLayerPush({ event, service, breed });
    }
};

export const trackUpcomingApptClick = (props: Pick<AppointmentEventProps, 'service' | 'selection'>) =>
    trackAppointmentEvent('click_upcoming_appt', props);

export const trackApptConfirmClick = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('confirm_appt', props);

export const trackRescheduleApptClick = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('reschedule_appt', props);

export const trackApptCancellationSuccess = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('cancel_appt', props);

export const trackKeepApptClick = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('keep_appt', props);

export const trackRebookApptClick = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('rebook_appt', props);

export const trackRedeemApptClick = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('redeem_appt', props);

export const trackDeliveryNotAvailable = (zipCode: string) => {
    dataLayerPush({
        event: 'sched-deliv-not-available',
        'sched-deliv-not-available-zipcode': zipCode,
    });
};

export const trackSelectMailIn = (sku: string) => {
    dataLayerPush({
        event: 'mail-in rx click',
        'mail-in rx click-SKU': sku,
    });
};

export const trackProductClickInCart = ({
    item,
    list = 'cart',
    variant = 'traditional',
}: {
    item: ItemUI;
    list?: string;
    variant?: string;
}) => {
    const product = {
        id: item.sku,
        brand: item.merchandiseBrand,
        price: item.totalPrice,
        name: item.name,
        dimension5: 'Other',
        dimension6: list,
        dimension41: item.mhVicePresidentName,
        dimension42: item.mhDivision,
        dimension43: item.mhDepartment,
        dimension44: item.mhClass,
        dimension45: item.mhCategory,
        position: item.positionWithinCart,
    };

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'EEproductClick',
        ecommerce: {
            currencyCode,
            click: {
                actionField: {
                    list,
                },
                products: [product],
            },
        },
        variant,
    });
};

export const trackNavigationClick = (selection: string, linkUrl: string) => {
    dataLayerPush({
        event: 'nav_click',
        nav_section: 'my account nav',
        selection,
        link_url: linkUrl,
    });
};

export const trackNavigationHeaderClick = (selection: string, linkUrl: string, navSection: string = 'header nav') => {
    dataLayerPush({
        event: 'nav_click',
        nav_section: navSection,
        selection,
        link_url: linkUrl,
    });
};

export const trackAutoshipShopNow = (linkText: string, linkUrl: string) => {
    dataLayerPush({
        event: 'click_to_shop',
        link_text: linkText,
        link_url: linkUrl,
        site_section: 'my account | autoship',
    });
};

export const trackChangeSubscriptionDate = (subscriptionDayOfMonth: number) => {
    dataLayerPush({
        event: 'change_subscription_date',
        subscription_day_of_month: subscriptionDayOfMonth,
    });
};

const formatFrequency = (frequency: number | null) => {
    if (!frequency) return null;
    const isWeek = frequency >= 7;
    const isMonth = frequency >= 90;

    let period: string = 'day';
    let frequencyValue: number = frequency;

    if (isMonth) {
        period = 'month';
        frequencyValue = frequency / 30;
    } else if (isWeek) {
        period = 'week';
        frequencyValue = frequency / 7;
    }

    return `every ${frequencyValue} ${period}${frequencyValue > 1 ? 's' : ''}`;
};

export const trackAutoship = (
    event: string,
    value: number | null,
    subscriptionId: string | null,
    currency: string | null,
    subscriptionFrequency: number | null,
    productId: string | null,
    productPrice: number | string | null,
    productQuantity: number | null,
    additionalData: Record<string, unknown> = {}
) => {
    dataLayerPush({
        event,
        value,
        currency,
        subscription_id: subscriptionId,
        product_id: productId,
        product_price: productPrice,
        ...(event !== 'change_subscription_frequency'
            ? {
                  subscription_frequency: formatFrequency(subscriptionFrequency),
              }
            : {
                  new_selection: formatFrequency(subscriptionFrequency),
              }),
        ...(event !== 'change_subscription_quantity' && { product_quantity: productQuantity }),
        ...additionalData,
    });
};
